import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonLink, Card, Heading, Text } from '@bn-apps/dim-ui-react';
import { ContentCopy } from '@bn-apps/design-system-icons';
import { useAuth } from '@hooks/useAuth';
import type { Tool } from '@/types';
import styles from './ToolCard.module.scss';

interface ToolCardProps {
  tool: Tool;
}

/**
 * ToolCard component - Displays a knowledge graph tool with access link
 * Uses Banenor Design System components and utility classes
 */
export default function ToolCard({ tool }: ToolCardProps): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [copySuccess, setCopySuccess] = useState(false);

  const { id, name, description, url, environments, icon, requiredAccessLevel, status, actionType = 'open' } = tool;

  // Check if user has required access
  const hasAccess =
    user &&
    (requiredAccessLevel === 'authenticated'
      ? user.isAuthenticated
      : user.isProjectMember);

  // Determine if tool is available
  const isAvailable = status !== 'setup-pending';
  const isDisabled = !hasAccess || !isAvailable;

  // Handle copy URL to clipboard
  const handleCopyUrl = async () => {
    if (!url) return;
    try {
      // Construct full URL if it's a relative path
      const fullUrl = url.startsWith('/') ? window.location.origin + url : url;
      await navigator.clipboard.writeText(fullUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <Card
      // Target of the /tools#<id> links of the quick find
      id={id}
      elevation="raised"
      padding="lg"
      gap="none"
      className={`${styles.toolCard} ${isDisabled ? styles.disabled : ''}`}
    >
      <div className={styles.icon} aria-hidden="true">
        {icon}
      </div>

      <div className={styles.content}>
        <Heading level={3} className={styles.name}>
          {name}
        </Heading>
        <Text className={styles.description}>
          {description}
        </Text>
      </div>

      {!isDisabled ? (
        <div className={styles.buttonContainer}>
          {environments ? (
            environments.map((env) => (
              <ButtonLink
                key={env.name}
                variant="primary"
                size="sm"
                href={env.url}
                isExternal
              >
                {env.name}
              </ButtonLink>
            ))
          ) : actionType === 'copy' ? (
            <Button
              variant="primary"
              size="sm"
              onClick={handleCopyUrl}
              iconEnd={ContentCopy}
            >
              {copySuccess ? t('tools.copied') : t('tools.copyUrl')}
            </Button>
          ) : (
            <ButtonLink
              variant="primary"
              size="sm"
              href={url}
              isExternal
            >
              {t('tools.open')}
            </ButtonLink>
          )}
        </div>
      ) : (
        <div className={styles.lockedMessage}>
          <span aria-hidden="true">🔒</span>
          <Text className={styles.lockedText}>
            {!hasAccess ? t('tools.accessRequired') : t('tools.setupPending')}
          </Text>
        </div>
      )}
    </Card>
  );
}
